/**
 * Page /numbering — Numérotation des tickets (serveur, onglet /manage).
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * Ce que le patient lit sur son ticket : numéro de service (clé d'appel), numéro d'ORDRE
 * GLOBAL (réassurance anti-confusion) et position instantanée. Persisté dans la config
 * serveur (PUT /api/config, via useConfig) et POUSSÉ au distributeur à l'impression —
 * exactement comme le son/voix. Même config éditable aussi depuis la console centrale.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useConfig } from '../../contexts/ConfigContext';

function NumberingSettings() {
  const navigate = useNavigate();
  const { config, updateConfig, loading } = useConfig();

  const [mode, setMode] = useState('service-only');
  const [showWaitingAhead, setShowWaitingAhead] = useState(false);
  const [waitingScope, setWaitingScope] = useState('service');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (loading) return;
    const n = config?.ticketNumbering || {};
    setMode(n.mode === 'global-only' || n.mode === 'global-plus-service' ? n.mode : 'service-only');
    setShowWaitingAhead(n.showWaitingAhead === true);
    setWaitingScope(n.waitingScope === 'global' ? 'global' : 'service');
  }, [loading, config]);

  const save = async () => {
    setSaving(true);
    // Fusion NON destructive : on n'écrase que la branche `ticketNumbering`.
    const merged = JSON.parse(JSON.stringify(config || {}));
    merged.ticketNumbering = { mode, showWaitingAhead, waitingScope };
    const ok = await updateConfig(merged);
    setSaving(false);
    if (ok) toast.success('Numérotation enregistrée');
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6 md:p-10">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">🔢 Numérotation des tickets</h1>
            <p className="text-sm text-gray-500">
              L'appel reste <strong>par service</strong> ; le numéro d'ordre global est une
              réassurance anti-confusion imprimée sur le ticket.
            </p>
          </div>
          <button onClick={() => navigate('/manage')} className="text-sm text-gray-600 hover:text-gray-900 underline">← Retour</button>
        </div>

        <div className="bg-white rounded-xl shadow p-6 border border-gray-100 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Affichage du numéro</label>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className="w-full p-2 border rounded-lg"
            >
              <option value="service-only">Numéro de service seul (ex. « C-007 »)</option>
              <option value="global-only">Numéro d'ordre global seul (ex. « 042 »)</option>
              <option value="global-plus-service">Les deux (ordre global + service)</option>
            </select>
          </div>

          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={showWaitingAhead}
              onChange={(e) => setShowWaitingAhead(e.target.checked)}
            />
            <span className="text-sm text-gray-700">Imprimer la position « ~N personnes avant vous »</span>
          </label>

          {showWaitingAhead && (
            <div className="ml-7">
              <label className="block text-sm font-medium text-gray-700 mb-1">Portée de la position</label>
              <select
                value={waitingScope}
                onChange={(e) => setWaitingScope(e.target.value)}
                className="w-full p-2 border rounded-lg"
              >
                <option value="service">Par service (« ~8 avant vous dans votre file »)</option>
                <option value="global">Global (« ~30 en attente au total »)</option>
              </select>
            </div>
          )}

          <div className="pt-2 border-t">
            <button
              onClick={save}
              disabled={saving || loading}
              className="px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Enregistrement…' : '💾 Enregistrer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NumberingSettings;
